// =====================================================================
// Faz AT (2026-05-07) — Admin Kullanıcı Yönetimi + RBAC
// =====================================================================
// Owner çoklu admin oluşturur, her birine sayfa-bazlı yetki verir.
//
// Endpoint:
//   GET    /api/admin/admin-users
//   GET    /api/admin/admin-users/permissions (PERMISSIONS + ROLE_PRESETS)
//   POST   /api/admin/admin-users (yeni admin)
//   PUT    /api/admin/admin-users/:id (perm/pw/is_active update)
//   DELETE /api/admin/admin-users/:id (soft deactivate)
//
// Süper admin yetkisi olmayan adminler bu sayfayı göremez (Layout filter).
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, Button, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Checkbox, FormControlLabel, Divider, Stack
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/Key";
import GroupIcon from "@mui/icons-material/Group";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("tr-TR") : "—";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});  // {key: label}
  const [presets, setPresets] = useState({});         // {role: {label, permissions}}
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", permissions: [] });
  const [saving, setSaving] = useState(false);

  // Edit permissions dialog
  const [editPermsDialog, setEditPermsDialog] = useState({ open: false, user: null, permissions: [] });
  // Change password dialog
  const [pwDialog, setPwDialog] = useState({ open: false, user: null, password: "" });

  const load = () => {
    setLoading(true);
    API.get("/api/admin/admin-users")
      .then(r => {
        setUsers(r.data.items || []);
        setPermissions(r.data.available_permissions || {});
        setPresets(r.data.presets || {});
        setErr(null);
      })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Yeni admin oluştur (gerekirse müşteriyi admin'e yükseltme onayı)
  const doSubmitCreate = async (promoteExisting = false) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (promoteExisting) payload.promote_existing = true;
      const r = await API.post("/api/admin/admin-users", payload);
      toast.success(r.data?.message || (r.data?.promoted ? "Müşteri admin'e yükseltildi" : "Yeni admin oluşturuldu"));
      setCreateOpen(false);
      setForm({ name: "", email: "", password: "", permissions: [] });
      load();
    } catch (e) {
      const data = e.response?.data;
      // Müşteri kayıtlı + promote teklifi → confirm dialog
      if (data?.conflict === 'customer_exists' && data?.can_promote && !promoteExisting) {
        const ok = window.confirm(
          `${form.email} adresi "${data.existing_user.role}" olarak kayıtlı.\n\n` +
          `Bu hesabı ADMİN'e yükseltmek ister misiniz?\n` +
          `(Sipariş geçmişi ve cari bilgileri korunur, sadece rol+şifre+yetki güncellenir.)`
        );
        if (ok) return doSubmitCreate(true);
        toast("İptal edildi", { icon: "ℹ️" });
        return;
      }
      toast.error(data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const submitCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Tüm alanlar zorunlu"); return;
    }
    if (form.password.length < 8) {
      toast.error("Şifre en az 8 karakter"); return;
    }
    return doSubmitCreate(false);
  };

  // Permission toggle (form veya edit dialog)
  const togglePermission = (perm, current, setter) => {
    setter(current.includes(perm) ? current.filter(p => p !== perm) : [...current, perm]);
  };

  // Preset uygula
  const applyPreset = (presetKey, current, setter) => {
    if (presets[presetKey]) {
      setter([...presets[presetKey].permissions]);
    }
  };

  // Edit yetki kaydet
  const saveEditPerms = async () => {
    try {
      await API.put(`/api/admin/admin-users/${editPermsDialog.user.id}`, {
        permissions: editPermsDialog.permissions
      });
      toast.success("Yetkiler güncellendi");
      setEditPermsDialog({ open: false, user: null, permissions: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  // Şifre değiştir
  const changePassword = async () => {
    if (pwDialog.password.length < 8) {
      toast.error("Şifre en az 8 karakter"); return;
    }
    try {
      await API.put(`/api/admin/admin-users/${pwDialog.user.id}`, {
        password: pwDialog.password
      });
      toast.success("Şifre değiştirildi");
      setPwDialog({ open: false, user: null, password: "" });
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  // Toggle is_active
  const toggleActive = async (user) => {
    try {
      await API.put(`/api/admin/admin-users/${user.id}`, {
        is_active: !user.is_active
      });
      toast.success(user.is_active ? "Devre dışı" : "Aktif");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  // Sil (soft)
  const remove = async (user) => {
    if (!window.confirm(`${user.name} adlı admin devre dışı bırakılsın mı?`)) return;
    try {
      await API.delete(`/api/admin/admin-users/${user.id}`);
      toast.success("Devre dışı bırakıldı");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  // Permission'ları kategoriye göre grupla (sayfa adı prefix bazında)
  const groupedPermissions = {};
  Object.entries(permissions).forEach(([key, label]) => {
    let group;
    if (key.startsWith("admin.")) group = "🔐 Süper Admin";
    else if (key.startsWith("page.")) {
      const sub = key.replace("page.", "");
      if (["dashboard", "cockpit", "action_center", "recommendations", "notes"].includes(sub)) group = "📊 Ana Yönetim";
      else if (["orders", "subscriptions", "products", "product360", "categories", "users", "suppliers", "stock", "cari", "import_export", "pending_users", "returns", "reviews", "quotes"].includes(sub)) group = "📦 Operasyon";
      else if (["reports", "pazar_analizi", "marketing", "email_campaign", "email_perf", "whatsapp", "drip", "push", "campaigns", "b2b_pricing", "coupons", "nps", "rfm", "cohort", "cross_sell", "funnel", "forecast", "sales_pattern", "category_sales", "site_traffic", "geo", "journey", "monthly_goals"].includes(sub)) group = "📈 Pazarlama / Analytics";
      else group = "⚙️ Sistem";
    } else group = "Diğer";
    if (!groupedPermissions[group]) groupedPermissions[group] = [];
    groupedPermissions[group].push({ key, label });
  });

  if (loading && users.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Admin Kullanıcı Yönetimi</Typography>
          <Typography variant="body2" color="text.secondary">
            Çoklu admin destek + sayfa-bazlı yetkilendirme
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Yenile"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Yeni Admin
          </Button>
        </Box>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* Bilgi */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Çoklu admin oluşturup her birine farklı yetkiler verebilirsin. Mikail Bey'e operasyon, pazarlamacıya kampanya, sana tam yetki gibi.
        <strong> "admin.full"</strong> yetkisi olan = süper admin (her şey görür).
      </Alert>

      {/* Admin tablosu */}
      <Card sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell><strong>Admin</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell align="center"><strong>Yetki Sayısı</strong></TableCell>
              <TableCell><strong>Eklendi</strong></TableCell>
              <TableCell align="center"><strong>Aktif</strong></TableCell>
              <TableCell align="right"><strong>İşlem</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>Hiç admin yok</TableCell></TableRow>
            )}
            {users.map(u => {
              const perms = u.permissions || [];
              const isSuper = perms.includes("admin.full");
              const isProtected = !!u.is_protected;  // 🔒 Owner ana hesap koruması
              return (
                <TableRow key={u.id} hover sx={{
                  opacity: u.is_active ? 1 : 0.5,
                  ...(isProtected && { bgcolor: "#fef3c7" })  // korumalı satır sarı vurgu
                }}>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography fontWeight={700} fontSize={14}>{u.name}</Typography>
                      {isProtected && (
                        <Tooltip title="🔒 Korumalı hesap — silinemez, deaktive edilemez, yetkisi düşürülemez">
                          <Chip label="🔒 KORUMALI" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#d97706", color: "white", fontWeight: 700 }} />
                        </Tooltip>
                      )}
                    </Stack>
                    {isSuper && (
                      <Chip label="🔐 SÜPER ADMIN" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#ef4444", color: "white", fontWeight: 700, mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell align="center">
                    {isSuper ? (
                      <Chip label="∞" size="small" sx={{ bgcolor: "#10b981", color: "white", fontWeight: 700 }} />
                    ) : (
                      <Chip label={`${perms.length} yetki`} size="small" />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.disabled" }}>{fmtDate(u.created_at)}</TableCell>
                  <TableCell align="center">
                    {/* Korumalı admin (owner ana hesabı) için Aktif/Pasif düğmesi
                        tamamen gizlenir — silinemez/devre-dışı bırakılamaz olduğu net görünsün. */}
                    {isProtected ? (
                      <Tooltip title="🔒 Korumalı hesap — kalıcı olarak aktif">
                        <Chip label="🔒 Kalıcı Aktif" size="small" sx={{
                          bgcolor: "#10b98122", color: "#059669", fontWeight: 700, fontSize: 11
                        }} />
                      </Tooltip>
                    ) : (
                      <Switch
                        checked={!!u.is_active}
                        onChange={() => toggleActive(u)}
                        color="success"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={isProtected ? "🔒 Korumalı — yetki değişmez" : "Yetkileri Düzenle"}>
                      <span>
                        <IconButton size="small"
                          disabled={isProtected}
                          onClick={() => setEditPermsDialog({ open: true, user: u, permissions: [...perms] })}
                          sx={{ color: "#1565C0" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Şifre Değiştir">
                      <IconButton size="small" onClick={() => setPwDialog({ open: true, user: u, password: "" })} sx={{ color: "#f59e0b" }}>
                        <KeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isProtected ? "🔒 Korumalı hesap silinemez" : "Devre Dışı Bırak"}>
                      <span>
                        <IconButton size="small"
                          disabled={isProtected}
                          onClick={() => remove(u)}
                          sx={{ color: "#ef4444" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Yeni Admin Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Yeni Admin Oluştur</DialogTitle>
        {/* dividers: title ile content arasına ayraç çizgisi + içeriğe doğru padding ekler
            (label'ler önceden DialogTitle'ın alt kenarına çarpıyordu — kesiliyordu) */}
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Ad Soyad *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email *" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Şifre * (en az 8 karakter)" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight={700} mb={1}>Hazır Rol Şablonları (Tıkla → permission'lar dolar)</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {Object.entries(presets).map(([key, p]) => (
              <Button
                key={key} size="small" variant="outlined"
                onClick={() => applyPreset(key, form.permissions, (p) => setForm({ ...form, permissions: p }))}
                sx={{ textAlign: "left" }}
              >
                <Box>
                  <Typography fontSize={12} fontWeight={700}>{p.label}</Typography>
                  <Typography fontSize={10} color="text.secondary">{p.description}</Typography>
                </Box>
              </Button>
            ))}
          </Box>

          <Typography variant="subtitle2" fontWeight={700} mt={2} mb={1}>
            Sayfa Yetkileri ({form.permissions.length} seçili)
          </Typography>
          {Object.entries(groupedPermissions).map(([group, items]) => (
            <Card key={group} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>{group}</Typography>
              <Grid container>
                {items.map(p => (
                  <Grid item xs={12} sm={6} md={4} key={p.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={form.permissions.includes(p.key)}
                          onChange={() => togglePermission(p.key, form.permissions, (perms) => setForm({ ...form, permissions: perms }))}
                        />
                      }
                      label={<Typography fontSize={12}>{p.label}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? "Oluşturuluyor..." : "Oluştur"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog open={editPermsDialog.open} onClose={() => setEditPermsDialog({ open: false, user: null, permissions: [] })} maxWidth="md" fullWidth>
        <DialogTitle>{editPermsDialog.user?.name} — Yetkileri Düzenle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {Object.entries(presets).map(([key, p]) => (
              <Button key={key} size="small" variant="outlined"
                onClick={() => applyPreset(key, editPermsDialog.permissions, (perms) => setEditPermsDialog({ ...editPermsDialog, permissions: perms }))}>
                {p.label}
              </Button>
            ))}
          </Box>
          {Object.entries(groupedPermissions).map(([group, items]) => (
            <Card key={group} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>{group}</Typography>
              <Grid container>
                {items.map(p => (
                  <Grid item xs={12} sm={6} md={4} key={p.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={editPermsDialog.permissions.includes(p.key)}
                          onChange={() => togglePermission(p.key, editPermsDialog.permissions, (perms) => setEditPermsDialog({ ...editPermsDialog, permissions: perms }))}
                        />
                      }
                      label={<Typography fontSize={12}>{p.label}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPermsDialog({ open: false, user: null, permissions: [] })}>İptal</Button>
          <Button variant="contained" onClick={saveEditPerms}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={pwDialog.open} onClose={() => setPwDialog({ open: false, user: null, password: "" })} maxWidth="xs" fullWidth>
        <DialogTitle>{pwDialog.user?.name} — Şifre Değiştir</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth type="password" label="Yeni Şifre (en az 8 karakter)"
            value={pwDialog.password}
            onChange={e => setPwDialog({ ...pwDialog, password: e.target.value })}
            autoFocus sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwDialog({ open: false, user: null, password: "" })}>İptal</Button>
          <Button variant="contained" onClick={changePassword}>Şifreyi Değiştir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
